import { DOCUMENT, isPlatformBrowser, isPlatformServer } from '@angular/common';
import { Injectable, Inject, PLATFORM_ID } from '@angular/core';

type FragmentKey = 'organization' | 'website' | 'product' | 'collection' | 'breadcrumbs';
export type FragmentTuple = [FragmentKey, object];

@Injectable({ providedIn: 'root' })
export class StructuredDataService {
    private readonly TAG_ID = 'ld-json';
    private fragments: Partial<Record<FragmentKey, object>> = {};

    constructor(@Inject(DOCUMENT) private doc: Document,
              @Inject(PLATFORM_ID) private pid: object) {}

    replaceAll(entries: FragmentTuple[]): void {
        this.fragments = entries.reduce<Record<string, object>>(
            (acc, [key, frag]) => {
                acc[key] = frag;
                return acc;
            },
            {}
        );
        this.render();
    }

    /** Add or overwrite a named fragment, then re-render */
    replaceOne(key: FragmentKey, json: object): void {
        this.fragments[key] = json;
        this.render();
    }

    /** Remove a fragment by its key */
    remove(key: FragmentKey): void {
        delete this.fragments[key];
        this.render();
    }

    /** Remove every fragment (call on NavigationStart) */
    clear(): void {
        this.fragments = {};
        this.render();
    }

    /** Internal: write the <script> tag once, keep its textContent fresh */
    private render(): void {
        // Don’t run in WebWorkers or other non-DOM platforms
        if (!isPlatformBrowser(this.pid) && !isPlatformServer(this.pid)) { return; }

        let el = this.doc.getElementById(this.TAG_ID) as HTMLScriptElement | null;
        if (!el) {
            el = this.doc.createElement('script');
            el.type = 'application/ld+json';
            el.id = this.TAG_ID;
            this.doc.head.appendChild(el);
        }

        // Convert the fragment map into 1 object or an array (both are valid) –
        // Google accepts either pattern :contentReference[oaicite:1]{index=1}
        const list = Object.values(this.fragments);
        el.textContent = JSON.stringify(list.length === 1 ? list[0] : list, null, 2);
    }
}
